import axios from "axios";
const SDK_LOG_PREFIX = "[VerificationSDK]";
function sdkLog(...args: any[]) {
  try { console.log(SDK_LOG_PREFIX, ...args); } catch {}
}
export interface InitOptions {
  reference?: string; // Unique reference from org
  email?: string;
  callback_url?: string; // Where server posts results
  redirect_url?: string; // Where user is redirected after KYC
  device_type?: "WEB" | "MOBILE"; // Optional
  // Optional default UI configuration for SDK-driven components
  ui?: {
    classNames?: Record<string, string>;
    styles?: Record<string, any>;
  };
}

export interface InitResponseData {
  session_id: string;
  device_type: "WEB" | "MOBILE";
  next_step: string;
  resume: boolean;
  verification_url: string;
  qrcode_url?: string;
  callback_url: string;
  redirect_url: string;
  expired_at: string;
}

export interface InitResponse {
  status: string; // e.g., "success" | "error"
  message: string;
  data: InitResponseData;
}


// Default API base URL (can be overridden via constructor)
// const DEFAULT_API_BASE_URL = "https://f4bc2a63837d.ngrok-free.app";
const DEFAULT_API_BASE_URL = "https://api.probusiness.astraprotocol.com";

// Utility to detect device type
function detectDeviceType(): "WEB" | "MOBILE" {
  if (typeof navigator !== "undefined") {
    const ua = navigator.userAgent || navigator.vendor || (window as any).opera || "";
    if (/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua)) {
      return "MOBILE";
    }
  }
  return "WEB";
}

export default class VerificationSDK {
  public serverKey: string;
  public sessionId: string | null = null;
  public deviceType: "WEB" | "MOBILE" = detectDeviceType();
  public qrCodeUrl: string | null = null;
  public uiConfig: { classNames?: Record<string, string>; styles?: Record<string, any> } = {};
  public static defaultUi: { classNames?: Record<string, string>; styles?: Record<string, any> } | null = null;
  public apiBaseUrl: string = DEFAULT_API_BASE_URL;

  constructor({ serverKey, apiBaseUrl }: { serverKey: string; apiBaseUrl?: string }) {
    this.serverKey = serverKey;
    if (apiBaseUrl) this.apiBaseUrl = apiBaseUrl;
    sdkLog("constructor", { hasServerKey: Boolean(serverKey), apiBaseUrl: this.apiBaseUrl });
  }

  /**
   * Expose server key so web can embed it in QR for mobile handoff
   */
  getServerKey(): string {
    sdkLog("getServerKey");
    return this.serverKey;
  }

  /**
   * Set session ID for existing sessions (used for mobile continuation)
   */
  setSessionId(sessionId: string): void {
    sdkLog("setSessionId", { sessionId });
    this.sessionId = sessionId;
  }

  /**
   * Clear the active session and related cached artifacts
   */
  resetSession(): void {
    sdkLog("resetSession", { hadSession: Boolean(this.sessionId) });
    this.sessionId = null;

    const storage =
      typeof window !== "undefined" && typeof window.localStorage !== "undefined"
        ? window.localStorage
        : null;

    if (storage) {
      try {
        storage.removeItem("kyc_session_id");
        storage.removeItem("kyc_face_done");
        storage.removeItem("kyc_qrcode_url");
      } catch (error: any) {
        sdkLog("resetSession:storageError", error?.message || error);
      }
    }
  }

  /**
   * Get QR code URL for mobile switching (if available from init response)
   */
  getQrCodeUrl(): string | null {
    sdkLog("getQrCodeUrl", { qrCodeUrl: this.qrCodeUrl });
    return this.qrCodeUrl;
  }

  /**
   * Set QR code URL from init response
   */
  setQrCodeUrl(url: string): void {
    sdkLog("setQrCodeUrl", { url });
    this.qrCodeUrl = url;
  }

  /**
   * Get current session ID
   */
  getSessionId(): string | null {
    sdkLog("getSessionId", { sessionId: this.sessionId });
    return this.sessionId;
  }

  /**
   * Step 1: Initialize session
   */

  async init(options: InitOptions): Promise<InitResponse> {
    sdkLog("init:start", { options });
    const deviceType = options?.device_type || detectDeviceType();
    this.deviceType = deviceType;
    const hasBody = Boolean(
      options && (options.reference || options.email || options.callback_url || options.redirect_url || options.ui)
    );

    try {
      const res = await axios.post<InitResponse>(
        `${this.apiBaseUrl}/api/v2/dashboard/merchant/onsite/initiate`,
        hasBody ? options : undefined,
        {
          headers: {
            "Content-Type": "application/json",
            "x-server-key": this.serverKey,
            "device-type": deviceType,
          },
          withCredentials: true,
        }
      );

      const json = res.data;

      if (!json || !json.data || !json.data.session_id) {
        throw new Error("Init failed: Invalid server response");
      }

      this.sessionId = json.data.session_id;
      this.deviceType = json.data.device_type || deviceType;

      if (options.ui) {
        const merged = {
          classNames: { ...(options.ui.classNames || {}) },
          styles: { ...(options.ui.styles || {}) },
        };
        this.uiConfig = merged;
        VerificationSDK.defaultUi = merged;
      } else if (VerificationSDK.defaultUi) {
        this.uiConfig = VerificationSDK.defaultUi;
      }

      if (json.data.qrcode_url) {
        this.setQrCodeUrl(json.data.qrcode_url);
      }

      sdkLog("init:success", { sessionId: this.sessionId, deviceType: this.deviceType, hasQr: Boolean(json.data.qrcode_url) });
      return json;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Init failed";
      sdkLog("init:error", msg);
      throw new Error(`Init failed: ${msg}`);
    }
  }

  /**
   * Step 2: Upload face scan
   */
  async face(faceBlob: Blob, serverKey: any): Promise<any> {
    sdkLog("face:start", { hasSession: Boolean(this.serverKey) });
    if (!this.sessionId) throw new Error("Session not initialized");

    const formData = new FormData();
    const faceFileName =
      (faceBlob as File)?.name || `face-${Date.now()}.jpg`;
    formData.append("face_scan_img", faceBlob, faceFileName);

    try {
      const res = await axios.post(
        `${this.apiBaseUrl}/api/v2/dashboard/merchant/onsite/session/${this.sessionId}/face`,
        formData,
        {
          headers: {
            "x-server-key": serverKey,
            "device-type": this.deviceType || detectDeviceType(),
          },
          withCredentials: true,
        }
      );
      sdkLog("face:success");
      return res.data;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Face upload failed";
      sdkLog("face:error", msg);
      throw new Error(`Face upload failed: ${msg}`);
    }
  }

  /**
   * Backwards-compatible alias to match UI usage
   */
  async uploadFace(faceBlob: Blob,serverKey: any): Promise<any> {
    console.log("showing face id in upload face in 1: ",serverKey)
    return this.face(faceBlob, serverKey);
  }

  /**
   * Step 3: Upload document (ID_CARD, PASSPORT, etc.)
   */
  async uploadDocument(docBlob: Blob, type: string, serverKey:any): Promise<any> {
    sdkLog("uploadDocument:start", { hasSession: Boolean(this.sessionId), type });
    if (!this.sessionId) throw new Error("Session not initialized");

    const formData = new FormData();
    const docFileName =
      (docBlob as File)?.name || `document-${Date.now()}.jpg`;
    formData.append("docs_scan_img", docBlob, docFileName);
    formData.append("docType", type);

    try {
      const res = await axios.post(
        `${this.apiBaseUrl}/api/v2/dashboard/merchant/onsite/session/${this.sessionId}/docs`,
        formData,
        {
          headers: {
            "x-server-key": serverKey,
            "device-type": this.deviceType || detectDeviceType(),
          },
          withCredentials: true,
        }
      );
      sdkLog("uploadDocument:success");
      return res.data;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Document upload failed";
      sdkLog("uploadDocument:error", msg);
      throw new Error(`Document upload failed: ${msg}`);
    }
  }

  // danish

  /**
   * Step 4: Check verification status
   */
  async getStatus(serverKey:any): Promise<any> {
    sdkLog("getStatus:start", { hasSession: Boolean(this.sessionId) });
    if (!this.sessionId) throw new Error("Session not initialized");
    try {
      const res = await axios.get(
        `${this.apiBaseUrl}/api/v2/dashboard/merchant/onsite/session/${this.sessionId}/status`,
        {
          headers: {
            "x-server-key": serverKey,
            "device-type": this.deviceType || detectDeviceType(),
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );
      sdkLog("getStatus:success");
      return res.data;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Status fetch failed";
      sdkLog("getStatus:error", msg);
      throw new Error(`Status fetch failed: ${msg}`);
    }
  }

  /**
   * Step 5: Fetch final KYC result for the session
   */
  async getResult(serverKey:any): Promise<any> {
    sdkLog("getResult:start", { hasSession: Boolean(this.sessionId) });
    if (!this.sessionId) throw new Error("Session not initialized");
    try {
      const res = await axios.get(
        `${this.apiBaseUrl}/api/v2/dashboard/merchant/onsite/session/${this.sessionId}/result`,
        {
          headers: {
            "x-server-key": serverKey,
            "device-type": this.deviceType || detectDeviceType(),
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );
      sdkLog("getResult:success");
      return res.data;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Result fetch failed";
      sdkLog("getResult:error", msg);
      throw new Error(`Result fetch failed: ${msg}`);
    }
  }

  /**
   * Retry an onsite KYC session (e.g. when face is already registered)
   */
  async retrySession(serverKey: any): Promise<any> {
    sdkLog("retrySession:start", { hasSession: Boolean(this.sessionId) });
    if (!this.sessionId) throw new Error("Session not initialized");

    try {
      const res = await axios.post(
        `${this.apiBaseUrl}/api/v2/dashboard/merchant/onsite/session/${this.sessionId}/retry`,
        undefined,
        {
          headers: {
            "x-server-key": serverKey,
            "device-type": this.deviceType || detectDeviceType(),
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );
      sdkLog("retrySession:success");
      return res.data;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Retry failed";
      sdkLog("retrySession:error", msg);
      throw new Error(`Retry failed: ${msg}`);
    }
  }

  /**
   * Get default UI configuration provided at init
   */
  getUiConfig(): { classNames?: Record<string, string>; styles?: Record<string, any> } {
    // Prefer per-instance overrides, fallback to global default to keep consistency
    return this.uiConfig || VerificationSDK.defaultUi || {};
  }
}
